/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import {
	ActionIcon,
	Avatar,
	Box,
	Group,
	Paper,
	Skeleton,
	Text,
	Image,
	Alert,
} from '@mantine/core';
import {
	useElementSize,
	useMediaQuery,
} from '@mantine/hooks';

// Styles
import classes from './additionality-project-details.module.css';

// Router
import { useParams } from 'react-router';

// I18n
import { useTranslation } from 'react-i18next';

// Api
import { useAdditionalityProject } from '@features/catalogs/additionality-projects/details/api/get-additionality-project-by-id';

// Components
import { Button } from '@components/atoms';

// Icons
import {
	IconCirclePlus,
	IconEdit,
	IconDownload,
	IconQuestionMark,
	IconEye,
} from '@tabler/icons-react';
import { IconInfoCircle } from '@tabler/icons-react';
import LinkedinLogo from '@assets/icons/linkedin-button.svg';
import XLogo from '@assets/icons/x-button.svg';

// constants
import {
	LINKEDIN_URL,
	NOT_APPLICABLE,
	X_URL,
} from '@config/constants/common';
import { getCountryName } from '@utils/countries';
import { OrganizationProject } from '@components/organisms/OrganizationProject';
import { queryClient } from '@lib/react-query';
import useStore from '@store/index';
import { UpdateAdditionalityProject } from '@features/catalogs/additionality-projects/list/components/UpdateAdditionalityProject';
import { TFileMetadata } from '@global-types/index';
import {
	Authorization,
	useAuthorization,
} from '@lib/authorization';
import { ROLES } from '@config/constants/roles';
import isNil from 'lodash/isNil';
import './additionality-project-details.css';
import { DescriptionRenderer } from '@components/atoms/DescriptionRenderer';
import { ACLS } from '@config/constants/acls';
/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const AdditionalityProjectDetails = () => {
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();

	const isBigScreen = useMediaQuery('(min-width: 120em)');

	const { ref } = useElementSize();

	const { projectId } = useParams();

	const { checkAccess, checkAcl } = useAuthorization();

	const {
		setUpdateAdditionalityProjectFormOpened,
		updateAdditionalityProjectFormOpened,
		getUser,
	} = useStore();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const userData: any = getUser();

	const icon = <IconInfoCircle />;

	const {
		data,
		user,
		isFetching,
		isError,
		contactProducer,
	} = useAdditionalityProject({
		projectId: projectId ? projectId : '',
	});

	const handleOpenLink = (
		url: string,
		socialMedia: string
	) => {
		switch (socialMedia) {
			case 'linkedin':
				window.open(`${LINKEDIN_URL}/${url}`, '_blank');
				break;
			case 'x':
				window.open(`${X_URL}/${url}`, '_blank');
				break;

			default:
				break;
		}
	};

	if (isError) {
		return (
			<Box className={classes.container} ref={ref}>
				<Paper className={classes.paperError}>
					<Alert
						variant="light"
						color="error"
						title={t('errors.invalidProjectIdTitle')}
						icon={icon}
					>
						{t('errors.invalidProjectId')}
					</Alert>
				</Paper>
			</Box>
		);
	}

	if (isFetching) {
		return (
			<Box className={classes.containerSkeletons}>
				<Skeleton
					height={'30%'}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'200px'}
					radius="md"
				/>
				<Skeleton
					height={'5%'}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'3%'}
					width={'50%'}
					radius="md"
				/>
				<Skeleton
					height={'8%'}
					width={'100%'}
					radius="md"
				/>
				<Skeleton
					height={'5%'}
					width={'100%'}
					radius="md"
				/>
			</Box>
		);
	}

	const additionalityProject = data;

	if (!additionalityProject) return null;

	const {
		cover,
		name,
		dueDate,
		technology,
		targetEuro,
		description,
		organization,
		entity,
		location,
		social,
		coverage,
		publicSpecs,
		internalSpecs,
		supportedBy,
	} = additionalityProject;

	return (
		<Box className={classes.container} ref={ref}>
			<UpdateAdditionalityProject
				onClose={() => {
					setUpdateAdditionalityProjectFormOpened(false);
				}}
				opened={updateAdditionalityProjectFormOpened}
			/>
			<Paper className={classes.paper}>
				<Box className={classes.wrapper}>
					<Box className={classes.wrapperLeft}>
						{/*Bloc Organization */}
						<OrganizationProject
							contactProducer={contactProducer}
							entity={entity}
							owner={user}
							user={userData}
							organization={organization}
							cover={cover}
							supportedBy={supportedBy}
						/>
						{/*Bloc Project informations */}
						<Box className={classes.bloc2}>
							<Box className={classes.rowHeader}>
								<Text className={classes.title}>
									{name}
								</Text>
								<Authorization
									allowedRoles={[
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
										ROLES.STAFF,
									]}
								>
									<Group gap={'12.35px'}>
										<Button
											variant="outline"
											disabled
											leftSection={<IconEye size={23} />}
											size={isBigScreen ? 'md' : 'sm'}
										>
											{t('global.transactionHistory')}
										</Button>
										{checkAcl(
											ACLS.ADDITIONALITIES.UPDATE
										) && (
											<ActionIcon
												onClick={() => {
													setUpdateAdditionalityProjectFormOpened(
														true
													);
													queryClient.invalidateQueries({
														queryKey: [
															'measurability-project-update',
															projectId,
														],
													});
												}}
												radius={'7px'}
												variant="outline"
												className={classes.actionIcon}
												aria-label="Settings"
											>
												<IconEdit stroke={1.67} />
											</ActionIcon>
										)}
										<Button
											disabled
											leftSection={
												<IconCirclePlus size={23} />
											}
											size={isBigScreen ? 'md' : 'sm'}
										>
											{t('global.contribute')}
										</Button>
									</Group>{' '}
								</Authorization>
							</Box>
							<Box className={classes.bloc2Informations}>
								<Box
									className={
										classes.groupCountryInformations
									}
								>
									<Avatar
										className={classes.logoCountry}
										radius={'xl'}
										src={`https://flagcdn.com/w40/${location.countryCode.toLowerCase()}.png`}
										size={'xs'}
									>
										<IconQuestionMark />
									</Avatar>

									<Box
										className={
											classes.groupCountryTextInformations
										}
									>
										<Text
											className={
												classes.textCountryProject
											}
										>
											{getCountryName(
												location.countryCode
											)}
										</Text>
										<Text
											className={
												classes.textCountryPlaceProject
											}
										>
											{`/ ${location.place ? location.place : NOT_APPLICABLE}`}
										</Text>
									</Box>
								</Box>
								{/* Due date */}
								<Box className={classes.rowData}>
									<Box className={classes.containerLabel}>
										<Text className={classes.labelText}>
											{t('global.dueDate')}
										</Text>
									</Box>
									<Box className={classes.containerValue}>
										<Text className={classes.valueText}>
											{dueDate}
										</Text>
									</Box>
								</Box>

								{/* Technology */}
								<Box className={classes.rowData}>
									<Box className={classes.containerLabel}>
										<Text className={classes.labelText}>
											{t('global.projectType')}
										</Text>
									</Box>
									<Box className={classes.containerValue}>
										<Text className={classes.valueText}>
											{t(technology)}
										</Text>
									</Box>
								</Box>
								{/* Financal Needs */}
								<Box className={classes.rowData}>
									<Box className={classes.containerLabel}>
										<Text className={classes.labelText}>
											{t('global.financialNeeds')}
										</Text>
									</Box>
									<Box className={classes.containerValue}>
										<Text className={classes.valueText}>
											{targetEuro}
										</Text>
									</Box>
								</Box>

								<Box className={classes.rowData}>
									<Box className={classes.containerLabel}>
										<Text className={classes.labelText}>
											{t('global.coverage')}
										</Text>
									</Box>
									<Box className={classes.containerValue}>
										<Text className={classes.valueText}>
											{coverage}
										</Text>
									</Box>
								</Box>
								{/* Social media */}
								{social &&
									(social.linkedIn || social.twitter) && (
										<Box className={classes.rowData}>
											<Box
												className={classes.containerLabel}
											>
												<Text
													className={classes.labelText}
												>
													{t('global.socialMedia')}
												</Text>
											</Box>
											<Box
												className={classes.containerValue}
											>
												<Group gap={'13.04px'}>
													{social.linkedIn && (
														<Image
															className={
																classes.socialMediaButton
															}
															fit="contain"
															src={LinkedinLogo}
															onClick={() =>
																handleOpenLink(
																	social.linkedIn,
																	'linkedin'
																)
															}
														/>
													)}
													{social.twitter && (
														<Image
															className={
																classes.socialMediaButton
															}
															fit="contain"
															src={XLogo}
															onClick={() =>
																handleOpenLink(
																	social.twitter,
																	'x'
																)
															}
														/>
													)}
												</Group>
											</Box>
										</Box>
									)}

								{/*Technical documents */}
								{checkAccess({
									allowedRoles: [
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
										ROLES.STAFF,
										ROLES.USER,
									],
								}) && (
									<Box
										className={
											classes.rowDataTechnicalDocument
										}
									>
										<Box
											className={classes.containerLabel}
										>
											<Text className={classes.labelText}>
												{t('global.technicalDocuments')}
											</Text>
										</Box>

										<Box
											className={classes.containerValue}
										>
											{publicSpecs &&
											Array.isArray(publicSpecs) &&
											publicSpecs.length > 0 ? (
												<Group>
													{publicSpecs.map(
														(
															fileMetaData: TFileMetadata
														) => (
															<Button
																key={fileMetaData.id}
																onClick={() => {
																	window.open(
																		fileMetaData.link,
																		'_blank'
																	);
																}}
																variant="outline"
																leftSection={
																	<IconDownload
																		size={15}
																	/>
																}
																size={'xs'}
															>
																{fileMetaData.filename}
															</Button>
														)
													)}
												</Group>
											) : (
												<Text
													className={classes.valueText}
												>
													{NOT_APPLICABLE}
												</Text>
											)}
										</Box>
									</Box>
								)}

								{/*Internal Technical documents */}
								{checkAccess({
									allowedRoles: [
										ROLES.ADMIN,
										ROLES.SUPERADMIN,
									],
								}) && (
									<Box
										className={
											classes.rowDataTechnicalDocument
										}
									>
										<Box
											className={classes.containerLabel}
										>
											<Text className={classes.labelText}>
												{t(
													'global.technicalDocumentsInternal'
												)}
											</Text>
										</Box>
										<Box
											className={classes.containerValue}
										>
											{!isNil(internalSpecs) &&
											internalSpecs &&
											Array.isArray(internalSpecs) &&
											internalSpecs.length > 0 ? (
												<Group>
													{internalSpecs.map(
														(
															fileMetaData: TFileMetadata
														) => (
															<Button
																key={fileMetaData.id}
																onClick={() => {
																	window.open(
																		fileMetaData.link,
																		'_blank'
																	);
																}}
																variant="outline"
																leftSection={
																	<IconDownload
																		size={15}
																	/>
																}
																size={'xs'}
															>
																{fileMetaData.filename}
															</Button>
														)
													)}
												</Group>
											) : (
												<Text
													className={classes.valueText}
												>
													{NOT_APPLICABLE}
												</Text>
											)}
										</Box>
									</Box>
								)}

								{/* Description */}
								<Box
									className={classes.rowDataDescription}
								>
									<Box
										className={
											classes.containerLabelDescription
										}
									>
										<Text className={classes.labelText}>
											{t('global.description')}
										</Text>
									</Box>
									<Box
										className={
											classes.containerValueDescription
										}
									>
										{description ? (
											<DescriptionRenderer
												description={description}
											/>
										) : (
											<Text className={classes.valueText}>
												{NOT_APPLICABLE}
											</Text>
										)}
									</Box>
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			</Paper>
		</Box>
	);
};

export default AdditionalityProjectDetails;
